import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Box,
  Typography,
  LinearProgress,
  IconButton,
  DialogProps,
} from '@mui/material'
import Slider from 'react-slick'
import 'slick-carousel/slick/slick.css'
import 'slick-carousel/slick/slick-theme.css'
import CloseIcon from '@mui/icons-material/Close'

export interface SlideStep {
  slideComponent: any
  actionsComponent: any
  slideName?: string
  subtitle?: string
}

export interface MultiSlideDialogProps {
  open: boolean
  onClose: () => void
  title: string
  steps: SlideStep[]
  maxWidth?: DialogProps['maxWidth']
  showProgressBar?: boolean
  slideProps?: Record<string, any>
  actionProps?: Record<string, any>
  dynamicMaxWidth?: (currentSlide: number) => DialogProps['maxWidth']
  onSlideChange?: (slideIndex: number) => void
}

function MultiSlideDialog({
  open,
  onClose,
  title,
  steps,
  maxWidth = 'sm',
  showProgressBar = true,
  slideProps = {},
  actionProps = {},
  dynamicMaxWidth,
  onSlideChange,
}: MultiSlideDialogProps) {
  const [currentSlide, setCurrentSlide] = useState(0)
  const sliderRef = useRef<Slider | null>(null)
  const dialogContentRef = useRef<HTMLDivElement | null>(null)
  const activeSlideRef = useRef<HTMLDivElement | null>(null)

  // Reset slide when dialog opens/closes
  useEffect(() => {
    if (!open) {
      setCurrentSlide(0)
    }
  }, [open])

  // Scroll to top when slide changes
  useEffect(() => {
    if (dialogContentRef.current) {
      dialogContentRef.current.scrollTop = 0
    }
    onSlideChange?.(currentSlide)
  }, [currentSlide, onSlideChange])

  const refreshSlickHeight = useCallback(() => {
    const sliderAny = sliderRef.current as any
    // react-slick only recalculates adaptiveHeight on resize / internal events.
    // When the active slide's content changes (conditional UI), we force a resize recalculation.
    sliderAny?.innerSlider?.onWindowResized?.()
  }, [])

  // Keep the dialog height in sync when the active slide's contents change size (e.g. toggles).
  useEffect(() => {
    if (!open) return
    if (typeof ResizeObserver === 'undefined') return
    if (!activeSlideRef.current) return

    const ro = new ResizeObserver(() => {
      requestAnimationFrame(refreshSlickHeight)
    })

    ro.observe(activeSlideRef.current)
    requestAnimationFrame(refreshSlickHeight)

    return () => ro.disconnect()
  }, [open, currentSlide, refreshSlickHeight])

  const handleNextStep = useCallback(() => {
    setCurrentSlide((prev) => {
      const next = prev + 1
      sliderRef.current?.slickNext()
      return next
    })
  }, [])

  const handlePreviousStep = useCallback(() => {
    setCurrentSlide((prev) => {
      const previous = prev - 1
      sliderRef.current?.slickPrev()
      return previous
    })
  }, [])

  const handleClose = useCallback(() => {
    setCurrentSlide(0)
    onClose()
  }, [onClose])

  const currentMaxWidth = useMemo(() => {
    return dynamicMaxWidth ? dynamicMaxWidth(currentSlide) : maxWidth
  }, [dynamicMaxWidth, currentSlide, maxWidth])

  const slickSettings = {
    dots: false,
    arrows: false,
    swipe: false,
    infinite: false,
    draggable: false,
    adaptiveHeight: true,
    slidesToShow: 1,
    slidesToScroll: 1,
  }

  const currentStep = steps[currentSlide]

  if (!steps.length) {
    return null
  }

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth={currentMaxWidth}>
      <DialogTitle>
        <Box display={'flex'} justifyContent={'space-between'} alignItems={'center'}>
          <Typography variant="h6">{title}</Typography>
          <IconButton onClick={handleClose}>
            <CloseIcon fontSize={'small'} />
          </IconButton>
        </Box>
      </DialogTitle>

      {showProgressBar && (
        <Box width={'100%'}>
          {currentSlide >= 1 && (
            <LinearProgress
              variant="determinate"
              value={((currentSlide - 1) / (steps.length - 2)) * 100}
              color={'success'}
            />
          )}
        </Box>
      )}

      <DialogContent
        ref={dialogContentRef}
        dividers
        sx={{ overflowY: 'auto', overflowX: 'hidden' }}
      >
        <Slider {...slickSettings} ref={sliderRef}>
          {steps.map((step, index) => (
            <Box
              key={index}
              ref={(el) => {
                if (index === currentSlide) activeSlideRef.current = el as HTMLDivElement
              }}
            >
              {step?.slideName && (
                <Box display={'flex'} flexDirection={'column'} mb={2} gap={1}>
                  <Typography fontWeight={'bold'}>{step.slideName}</Typography>
                  {step.subtitle && (
                    <Typography variant="body2" color="textSecondary">
                      {step.subtitle}
                    </Typography>
                  )}
                </Box>
              )}

              {React.createElement(step.slideComponent, {
                ...slideProps,
                currentSlide,
                totalSlides: steps.length,
              })}
            </Box>
          ))}
        </Slider>
      </DialogContent>

      <DialogActions sx={{ height: 40 }}>
        {React.createElement(currentStep.actionsComponent, {
          ...actionProps,
          onNextStep: handleNextStep,
          onPreviousStep: handlePreviousStep,
          onClose: handleClose,
          currentSlide,
          totalSlides: steps.length,
          isFirstSlide: currentSlide === 0,
          isLastSlide: currentSlide === steps.length - 1,
        })}
      </DialogActions>
    </Dialog>
  )
}

export default MultiSlideDialog
